import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Save, ArrowLeft, User, Building2, MapPin, CreditCard, Phone, FileText, GraduationCap, Briefcase, RefreshCw, Users, DollarSign, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PersonalTab from './employee-create-tabs/PersonalTab';
import OrganizationTab from './employee-create-tabs/OrganizationTab';
import EmploymentTab from './employee-create-tabs/EmploymentTab';
import CommunicationTab from './employee-create-tabs/CommunicationTab';
import AddressTab from './employee-create-tabs/AddressTab';
import BankTab from './employee-create-tabs/BankTab';
import DocumentsTab from './employee-create-tabs/DocumentsTab';
import EducationTab from './employee-create-tabs/EducationTab';
import WorktimeTab from './employee-create-tabs/WorktimeTab';
import PayTab from './employee-create-tabs/PayTab';
import FamilyTab from './employee-create-tabs/FamilyTab';
import RecurringTab from './employee-create-tabs/RecurringTab';
import AdditionalTab from './employee-create-tabs/AdditionalTab';
import { withStableUid, stripStableKeys } from '../../../hooks/useArrayWithStableKeys';

const EmployeeCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [companyCodes, setCompanyCodes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [personnelAreas, setPersonnelAreas] = useState([]);
  const [personnelSubareas, setPersonnelSubareas] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [employeeSubgroups, setEmployeeSubgroups] = useState([]);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    employee_id: '',
    // Personal Data (Infotype 0002)
    personal_data: {
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      gender: 'M',
      marital_status: 'S',
      nationality: '',
      birth_place: '',
      blood_group: ''
    },
    // Organizational Assignment (Infotype 0001)
    organizational_assignment: {
      company_code: '',
      personnel_area: '',
      personnel_subarea: '',
      business_area: '',
      organizational_unit: '',
      position: '',
      job: '',
      cost_center: '',
      employee_group: '',
      employee_subgroup: '',
      payroll_area: '',
      supervisor_id: ''
    },
    // Employment Info
    employment_info: {
      hire_date: new Date().toISOString().split('T')[0],
      confirmation_date: '',
      termination_date: '',
      employment_type: 'FT',
      probation_end_date: '',
      notice_period_days: 30,
      work_schedule: ''
    },
    // Communication (Infotype 0105)
    communication: {
      work_email: '',
      personal_email: '',
      work_phone: '',
      mobile_phone: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relation: ''
    },
    // Addresses (Infotype 0006)
    addresses: [{
      address_type: 'CURRENT',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      phone: '',
      is_primary: true
    }],
    // Bank Details (Infotype 0009)
    bank_details: [{
      bank_name: '',
      bank_branch: '',
      account_number: '',
      account_holder_name: '',
      ifsc_code: '',
      swift_code: '',
      account_type: 'Savings',
      is_primary: true
    }],
    // Identification Documents
    identification_documents: [{
      document_type: '',
      document_number: '',
      issue_date: '',
      expiry_date: '',
      issuing_authority: '',
      issuing_country: ''
    }],
    // Education
    education: [{
      degree: '',
      institution: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      grade: '',
      is_highest: false
    }],
    // Planned Working Time (Infotype 0007)
    planned_working_time: {
      work_schedule_rule: '',
      time_management_status: '1',
      working_hours_per_week: 40,
      working_hours_per_day: 8,
      working_days_per_week: 5,
      part_time_percentage: 100,
      employment_percent: 100,
      daily_working_hours: '09:00-18:00',
      time_recording_variant: ''
    },
    // Basic Pay (Infotype 0008)
    basic_pay: {
      pay_scale_type: '',
      pay_scale_area: '',
      pay_scale_group: '',
      pay_scale_level: '',
      basic_salary: '',
      currency: 'INR',
      pay_frequency: 'MONTHLY',
      annual_salary: '',
      hourly_rate: ''
    },
    // Family Members (Infotype 0021)
    family_members: [],
    // Recurring Payments/Deductions (Infotype 0014)
    recurring_payments: [],
    // Additional Payments (Infotype 0015)
    additional_payments: [],
    status: 'ACTIVE',
    remarks: ''
  });

  const loadMasterData = useCallback(async () => {
    try {
      const [companyRes, ccRes, paRes, psaRes, egRes, esgRes] = await Promise.all([
        axios.get(`${backendUrl}/api/company/company-codes`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/personnel-areas`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/personnel-subareas`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/employee-groups`).catch(() => ({ data: [] })),
        axios.get(`${backendUrl}/api/hcm/employee-subgroups`).catch(() => ({ data: [] }))
      ]);
      setCompanyCodes(companyRes.data);
      setCostCenters(ccRes.data);
      setPersonnelAreas(paRes.data.filter(pa => pa.status === 'Active'));
      setPersonnelSubareas(psaRes.data.filter(psa => psa.status === 'Active'));
      setEmployeeGroups(egRes.data.filter(eg => eg.status === 'Active'));
      setEmployeeSubgroups(esgRes.data.filter(esg => esg.status === 'Active'));
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, [backendUrl]);

  const generateEmployeeId = useCallback(async () => {
    try {
      setGeneratingId(true);
      const res = await axios.get(`${backendUrl}/api/hcm/employees/generate-id`);
      setFormData(prev => ({ ...prev, employee_id: res.data.employee_id }));
    } catch (error) {
      console.error('Error generating employee ID:', error);
    } finally {
      setGeneratingId(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    loadMasterData();
    generateEmployeeId();
  }, [loadMasterData, generateEmployeeId]);

  const handlePersonalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      personal_data: { ...prev.personal_data, [field]: value }
    }));
  };

  const handleOrgChange = (field, value) => {
    setFormData(prev => {
      const newOrgAssignment = { ...prev.organizational_assignment, [field]: value };
      
      // Cascade clear: if Personnel Area changes, clear Personnel Subarea
      if (field === 'personnel_area') {
        newOrgAssignment.personnel_subarea = '';
      }
      
      // Cascade clear: if Employee Group changes, clear Employee Subgroup
      if (field === 'employee_group') {
        newOrgAssignment.employee_subgroup = '';
      }
      
      return {
        ...prev,
        organizational_assignment: newOrgAssignment
      };
    });
  };

  const handleEmploymentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      employment_info: { ...prev.employment_info, [field]: value }
    }));
  };

  const handleCommChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      communication: { ...prev.communication, [field]: value }
    }));
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const newAddresses = [...prev.addresses];
      newAddresses[index] = { ...newAddresses[index], [field]: value };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleBankChange = (index, field, value) => {
    setFormData(prev => {
      const newBanks = [...prev.bank_details];
      newBanks[index] = { ...newBanks[index], [field]: value };
      return { ...prev, bank_details: newBanks };
    });
  };

  const handleDocChange = (index, field, value) => {
    setFormData(prev => {
      const newDocs = [...prev.identification_documents];
      newDocs[index] = { ...newDocs[index], [field]: value };
      return { ...prev, identification_documents: newDocs };
    });
  };

  const handleEducationChange = (index, field, value) => {
    setFormData(prev => {
      const newEdu = [...prev.education];
      newEdu[index] = { ...newEdu[index], [field]: value };
      return { ...prev, education: newEdu };
    });
  };

  const handleWorkingTimeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      planned_working_time: { ...prev.planned_working_time, [field]: value }
    }));
  };

  const handlePayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      basic_pay: { ...prev.basic_pay, [field]: value }
    }));
  };

  const handleAddFamilyMember = () => {
    setFormData(prev => ({
      ...prev,
      family_members: [...prev.family_members, withStableUid({
        relationship: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        is_dependent: true,
        is_emergency_contact: false,
        phone_number: '',
        health_insurance_covered: false,
        life_insurance_beneficiary: false
      })]
    }));
  };

  const handleFamilyMemberChange = (uid, field, value) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.map(m => m._uid === uid ? { ...m, [field]: value } : m)
    }));
  };

  const handleRemoveFamilyMember = (uid) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.filter(m => m._uid !== uid)
    }));
  };

  const handleAddRecurringPayment = () => {
    setFormData(prev => ({
      ...prev,
      recurring_payments: [...prev.recurring_payments, withStableUid({
        wage_type: '',
        wage_type_name: '',
        amount: '',
        currency: 'INR',
        payment_type: 'DEDUCTION',
        frequency: 'MONTHLY',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        number_of_installments: '',
        remarks: ''
      })]
    }));
  };

  const handleRecurringPaymentChange = (uid, field, value) => {
    setFormData(prev => ({
      ...prev,
      recurring_payments: prev.recurring_payments.map(p => p._uid === uid ? { ...p, [field]: value } : p)
    }));
  };

  const handleRemoveRecurringPayment = (uid) => {
    setFormData(prev => ({
      ...prev,
      recurring_payments: prev.recurring_payments.filter(p => p._uid !== uid)
    }));
  };

  const handleAddAdditionalPayment = () => {
    setFormData(prev => ({
      ...prev,
      additional_payments: [...prev.additional_payments, withStableUid({
        wage_type: '',
        wage_type_name: '',
        amount: '',
        currency: 'INR',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reason: '',
        remarks: '',
        is_taxable: true
      })]
    }));
  };

  const handleAdditionalPaymentChange = (uid, field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_payments: prev.additional_payments.map(p => p._uid === uid ? { ...p, [field]: value } : p)
    }));
  };

  const handleRemoveAdditionalPayment = (uid) => {
    setFormData(prev => ({
      ...prev,
      additional_payments: prev.additional_payments.filter(p => p._uid !== uid)
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.employee_id) {
      toast.error('Employee ID is required');
      return;
    }
    if (!formData.personal_data.first_name || !formData.personal_data.last_name) {
      toast.error('First name and last name are required');
      setActiveTab('personal');
      return;
    }
    if (!formData.personal_data.date_of_birth) {
      toast.error('Date of birth is required');
      setActiveTab('personal');
      return;
    }
    if (!formData.organizational_assignment.company_code) {
      toast.error('Company code is required');
      setActiveTab('organization');
      return;
    }
    if (!formData.employment_info.hire_date) {
      toast.error('Hire date is required');
      setActiveTab('employment');
      return;
    }

    try {
      setLoading(true);
      
      // Helper to convert empty strings to null for optional date fields
      const cleanDates = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        const cleaned = { ...obj };
        for (const key in cleaned) {
          if (cleaned[key] === '' && key.includes('date')) {
            cleaned[key] = null;
          } else if (typeof cleaned[key] === 'object' && cleaned[key] !== null) {
            cleaned[key] = cleanDates(cleaned[key]);
          }
        }
        return cleaned;
      };

      // Clean up empty arrays and convert empty date strings to null
      const payload = {
        ...formData,
        employment_info: cleanDates(formData.employment_info),
        personal_data: cleanDates(formData.personal_data),
        basic_pay: cleanDates(formData.basic_pay),
        addresses: formData.addresses.filter(a => a.address_line1).map(cleanDates),
        bank_details: formData.bank_details.filter(b => b.bank_name && b.account_number),
        identification_documents: formData.identification_documents.filter(d => d.document_type && d.document_number).map(cleanDates),
        education: formData.education.filter(e => e.degree && e.institution).map(cleanDates),
        family_members: stripStableKeys((formData.family_members || []).filter(f => f.first_name).map(cleanDates)),
        recurring_payments: stripStableKeys((formData.recurring_payments || []).map(cleanDates)),
        additional_payments: stripStableKeys(formData.additional_payments || [])
      };

      const res = await axios.post(`${backendUrl}/api/hcm/employees/`, payload);
      toast.success(res.data.message);
      navigate(`/hcm/employees/${res.data.employee.employee_id}`);
    } catch (error) {
      console.error('Error creating employee:', error);
      // Handle Pydantic validation errors (array of {loc, msg, type, ctx} objects)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Extract first error message
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create employee');
      }
    } finally {
      setLoading(false);
    }
  };

  // ctx bag — passed to all 13 extracted tabs
  const ctx = {
    formData, setFormData, generatingId,
    companyCodes, costCenters,
    personnelAreas, personnelSubareas,
    employeeGroups, employeeSubgroups,
    generateEmployeeId,
    handlePersonalChange, handleOrgChange, handleEmploymentChange,
    handleCommChange, handleAddressChange, handleBankChange,
    handleDocChange, handleEducationChange, handleWorkingTimeChange,
    handlePayChange,
    handleAddFamilyMember, handleFamilyMemberChange, handleRemoveFamilyMember,
    handleAddRecurringPayment, handleRecurringPaymentChange, handleRemoveRecurringPayment,
    handleAddAdditionalPayment, handleAdditionalPaymentChange, handleRemoveAdditionalPayment,
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="employee-create-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/hcm/employees')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Employee</h1>
              <p className="text-gray-500">Personnel Administration - New Employee Master Record</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Employee ID:</Label>
              <Input
                value={formData.employee_id}
                onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value.toUpperCase() }))}
                className="w-32 font-mono"
                placeholder="EMP000001"
              />
              <Button variant="outline" size="sm" onClick={generateEmployeeId} disabled={generatingId}>
                <RefreshCw className={`w-4 h-4 ${generatingId ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <Button 
              onClick={handleSubmit}
              disabled={loading}
              className="bg-stone-800 hover:bg-stone-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Employee'}
            </Button>
          </div>
        </div>

        {/* Form Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="mb-4 overflow-x-auto">
            <TabsList className="inline-flex min-w-full">
              <TabsTrigger value="personal" className="flex items-center gap-1 text-xs px-3">
                <User className="w-3 h-3" /> Personal
              </TabsTrigger>
              <TabsTrigger value="organization" className="flex items-center gap-1 text-xs px-3">
                <Building2 className="w-3 h-3" /> Org
              </TabsTrigger>
              <TabsTrigger value="employment" className="flex items-center gap-1 text-xs px-3">
                <Briefcase className="w-3 h-3" /> Employment
              </TabsTrigger>
              <TabsTrigger value="communication" className="flex items-center gap-1 text-xs px-3">
                <Phone className="w-3 h-3" /> Contact
              </TabsTrigger>
              <TabsTrigger value="address" className="flex items-center gap-1 text-xs px-3">
                <MapPin className="w-3 h-3" /> Address
              </TabsTrigger>
              <TabsTrigger value="bank" className="flex items-center gap-1 text-xs px-3">
                <CreditCard className="w-3 h-3" /> Bank
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex items-center gap-1 text-xs px-3">
                <FileText className="w-3 h-3" /> ID Docs
              </TabsTrigger>
              <TabsTrigger value="education" className="flex items-center gap-1 text-xs px-3">
                <GraduationCap className="w-3 h-3" /> Education
              </TabsTrigger>
              <TabsTrigger value="worktime" className="flex items-center gap-1 text-xs px-3">
                <Briefcase className="w-3 h-3" /> Time
              </TabsTrigger>
              <TabsTrigger value="pay" className="flex items-center gap-1 text-xs px-3">
                <CreditCard className="w-3 h-3" /> Pay
              </TabsTrigger>
              <TabsTrigger value="family" className="flex items-center gap-1 text-xs px-3">
                <Users className="w-3 h-3" /> Family
              </TabsTrigger>
              <TabsTrigger value="recurring" className="flex items-center gap-1 text-xs px-3">
                <DollarSign className="w-3 h-3" /> Recurring
              </TabsTrigger>
              <TabsTrigger value="additional" className="flex items-center gap-1 text-xs px-3">
                <Plus className="w-3 h-3" /> Additional
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Personal Data Tab */}
          <PersonalTab ctx={ctx} />

          {/* Organization Tab */}
          <OrganizationTab ctx={ctx} />

          {/* Employment Tab */}
          <EmploymentTab ctx={ctx} />

          {/* Communication Tab */}
          <CommunicationTab ctx={ctx} />

          {/* Address Tab */}
          <AddressTab ctx={ctx} />

          {/* Bank Tab */}
          <BankTab ctx={ctx} />

          {/* Documents Tab */}
          <DocumentsTab ctx={ctx} />

          {/* Education Tab */}
          <EducationTab ctx={ctx} />

          {/* Working Time Tab (Infotype 0007) */}
          <WorktimeTab ctx={ctx} />

          {/* Basic Pay Tab (Infotype 0008) */}
          <PayTab ctx={ctx} />

          {/* Family Members Tab (Infotype 0021) */}
          <FamilyTab ctx={ctx} />

          {/* Recurring Payments/Deductions Tab (Infotype 0014) */}
          <RecurringTab ctx={ctx} />

          {/* Additional Payments Tab (Infotype 0015) */}
          <AdditionalTab ctx={ctx} />
        </Tabs>

        {/* Remarks */}
        <Card>
          <CardContent className="pt-6">
            <Label>Additional Remarks</Label>
            <Textarea
              value={formData.remarks}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Any additional notes about the employee..."
              rows={3}
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeCreate;
